import React from "react";
import {
  Card,
  CardAuthor,
  CardContent,
} from "@/components/molecules/card/Card";
import { Column } from "@/components/molecules/column/Column";
import { CardGroup } from "@/components/molecules/dragndrop/CardGroup";
import { ColumnCards } from "@/components/molecules/dragndrop/ColumnCards";
import { DraggableCard } from "@/components/molecules/dragndrop/DraggableCard";
import { SLOT_MACHINE_ANIMATION_DURATION } from "@/components/organisms/slot_machine/SlotMachine";
import { useRetro } from "@/context/retro/RetroContext.hook";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";

export const GroupView: React.FC = () => {
  const {
    teamUsers,
    columns,
    cards,
    moveCard,
    slotMachineVisible,
    highlightedUserId,
  } = useRetro();
  const delayedHighlightedUserId = useDebounce(
    highlightedUserId,
    SLOT_MACHINE_ANIMATION_DURATION,
  );

  return (
    <div
      className={cn(
        "grid grid-flow-col [grid-auto-columns:minmax(300px,1fr)] h-full",
        slotMachineVisible && "pb-28",
      )}
    >
      {columns.map((column) => {
        const columnCards = cards.filter((c) => c.columnId === column.id);

        return (
          <Column
            key={column.id}
            columnData={{
              name: column.name,
              description: column.description,
            }}
          >
            <ColumnCards columnId={column.id} onCardDropped={moveCard}>
              {columnCards
                .filter((c) => c.parentCardId === null)
                .sort((a, b) => {
                  if (!slotMachineVisible || a.authorId === b.authorId) {
                    return 0;
                  }

                  if (a.authorId === delayedHighlightedUserId) {
                    return -1;
                  }

                  if (b.authorId === delayedHighlightedUserId) {
                    return 1;
                  }

                  return 0;
                })
                .map((group) => {
                  const groupCards = [
                    group,
                    ...cards.filter((c) => c.parentCardId === group.id),
                  ];

                  const isGroupHidden =
                    slotMachineVisible &&
                    delayedHighlightedUserId !== null &&
                    delayedHighlightedUserId !== group.authorId &&
                    !groupCards
                      .map((card) => card.authorId)
                      .includes(delayedHighlightedUserId);

                  return (
                    <CardGroup
                      key={group.id}
                      parentCardId={group.id}
                      columnId={column.id}
                      className={cn(
                        "transition-opacity",
                        isGroupHidden && "opacity-30",
                      )}
                      onCardDropped={moveCard}
                    >
                      {groupCards.map((card, index) => {
                        const user = teamUsers.find(
                          (user) => user.id === card.authorId,
                        );

                        return (
                          <DraggableCard
                            key={card.id}
                            parentCardId={card.parentCardId}
                            cardId={card.id}
                            columnId={column.id}
                            className={cn(index === 0 ? "mt-0" : "-mt-20")}
                          >
                            <Card id={card.id}>
                              <CardContent text={card.text} />
                              <CardAuthor
                                author={{
                                  avatar: user?.avatar_link || "",
                                  name: user?.nick || "",
                                  id: card.authorId,
                                }}
                              />
                            </Card>
                          </DraggableCard>
                        );
                      })}
                    </CardGroup>
                  );
                })}
            </ColumnCards>
          </Column>
        );
      })}
    </div>
  );
};
